import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useAppStore } from './stores/app'
import { BrandingProvider } from './components/BrandingProvider'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Layout } from './components/Layout'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ChatPage } from './pages/ChatPage'
import { PersonasPage } from './pages/PersonasPage'
import { ContentPage } from './pages/ContentPage'
import { ContentDetailPage } from './components/content/ContentDetailPage'
import { VisualPage } from './pages/VisualPage'
import { CostsPage } from './pages/CostsPage'
import { SettingsPage } from './pages/SettingsPage'
import { TemplatesPage } from './pages/TemplatesPage'
import { AIConfigPage } from './pages/AIConfigPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { InvitationPage } from './pages/InvitationPage'
import { BillingPage } from './pages/BillingPage'
import { BrandOnboardingPage } from './pages/BrandOnboardingPage'
import { BackofficeLayout } from './components/backoffice/BackofficeLayout'

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const organizationId = useAppStore((state) => state.organizationId)
  const currentUser = useAppStore((state) => state.currentUser)

  if (loading) return <LoadingScreen />

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Only redirect to onboarding once currentUser is loaded and has no org
  if (currentUser !== null && organizationId === null) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const currentUser = useAppStore((state) => state.currentUser)
  if (currentUser?.role !== 'admin') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth()
  const currentUser = useAppStore((state) => state.currentUser)
  if (loading) return <LoadingScreen />
  if (!currentUser?.is_super_admin) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
      <Route path="/invite/:token" element={<InvitationPage />} />

      <Route
        path="/backoffice/*"
        element={
          <RequireAuth>
            <RequireSuperAdmin>
              <BackofficeLayout />
            </RequireSuperAdmin>
          </RequireAuth>
        }
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <BrandingProvider>
              <ErrorBoundary fallbackTitle="Error en la aplicación">
                <Layout />
              </ErrorBoundary>
            </BrandingProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:conversationId" element={<ChatPage />} />
        <Route path="personas" element={<PersonasPage />} />
        <Route path="content" element={<ContentPage />} />
        <Route path="content/:taskId" element={<ContentDetailPage />} />
        <Route path="visual" element={<VisualPage />} />
        <Route path="costs" element={<CostsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="ai-config" element={<RequireAdmin><AIConfigPage /></RequireAdmin>} />
        <Route path="billing" element={<RequireAdmin><BillingPage /></RequireAdmin>} />
        <Route path="brand-onboarding" element={<BrandOnboardingPage />} />
      </Route>
    </Routes>
  )
}
